<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { SourceTypeChoice } from "@core/components/source/form/append/machine/source-append.machine"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Quel type de source souhaitez-vous ajouter ?",
            pdl: "Compteur électrique (PDL)",
            pce: "Compteur gaz (PCE)",
            source: "Capteur / Objet connecté",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
export interface SourceTypeSelectorProps {
    /**
     * Types de source autorisés. Si non fourni, tous les types sont affichés.
     */
    allowedTypes?: SourceTypeChoice[]
    /**
     * Désactive tous les choix (ex. plafond de sources atteint sur l’étape sélecteur).
     */
    disableAll?: boolean
    /**
     * Types désactivés individuellement (ex. ouverture directe avec plafond atteint).
     */
    disabledTypes?: SourceTypeChoice[]
}

interface Emits {
    /**
     * Émis lorsque l'utilisateur choisit un type (PDL, PCE ou Capteur)
     */
    (e: "submit", sourceType: SourceTypeChoice): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<SourceTypeSelectorProps>(), {
    allowedTypes: undefined,
    disableAll: false,
    disabledTypes: undefined,
})

const emit = defineEmits<Emits>()

// ================================
// DONNÉES CALCULÉES
// ================================

/** Toutes les options possibles pour le choix du type de source */
const allTypeOptions = [
    {
        label: t("pdl"),
        value: "pdl" as SourceTypeChoice,
        icon: "pi pi-bolt",
    },
    {
        label: t("pce"),
        value: "pce" as SourceTypeChoice,
        icon: "fa-light fa-fire-flame-simple",
    },
    {
        label: t("source"),
        value: "source" as SourceTypeChoice,
        icon: "pi pi-wifi",
    },
]

/**
 * Options affichées pour le choix du type de source (filtrées par allowedTypes si fourni)
 */
const getTypeOptions = computed(() => {
    if (!props.allowedTypes?.length) return allTypeOptions
    const allowedSet = new Set(props.allowedTypes)
    return allTypeOptions.filter((opt) => allowedSet.has(opt.value))
})

/**
 * Indique si un type de source doit être non cliquable
 */
function getIsOptionDisabled(value: SourceTypeChoice): boolean {
    if (props.disableAll) return true
    return props.disabledTypes?.includes(value) ?? false
}

// ================================
// INTERACTIONS
// ================================

/**
 * Gère le clic sur un type de source
 * @param sourceType - Type sélectionné (pdl, pce, source)
 */
function onSubmit(sourceType: SourceTypeChoice): void {
    emit("submit", sourceType)
}
</script>

<template>
    <div class="source-type-selector flex flex-col gap-6 w-full">
        <!-- ================================ -->
        <!-- TITRE -->
        <!-- ================================ -->
        <h2 class="text-lg font-semibold text-gray-700 text-center">
            {{ t("title") }}
        </h2>

        <!-- ================================ -->
        <!-- BOUTONS DE CHOIX -->
        <!-- ================================ -->
        <div class="flex flex-col gap-3">
            <BaseButton
                v-for="opt in getTypeOptions"
                :key="opt.value"
                :label="opt.label"
                icon-pos="left"
                class="w-full justify-start"
                severity="secondary"
                :disabled="getIsOptionDisabled(opt.value)"
                @click="onSubmit(opt.value)"
            >
                <template #icon>
                    <BaseIcon :icon="opt.icon" />
                </template>
            </BaseButton>
        </div>
    </div>
</template>
